import { z } from 'zod'
import { productSummarySchema } from './catalog.ts'
import { currencySchema, DEFAULT_VAT_BPS, taxBpsSchema } from './money.ts'
import { priceTierListSchema, priceTierSchema } from './pricing.ts'
import { unitCodeSchema } from './units.ts'

/**
 * The two sides of the platform's book.
 *
 * A SELLER LISTING is one shop's private offer — their cost, their stock, their
 * bulk breaks. An ADMIN LISTING is what customers actually see. They are separate
 * tables rather than one with a flag because the whole business is the spread
 * between them, and a buyer-facing response must be incapable of carrying a seller
 * column (docs/02-architecture.md §5).
 */

export const LISTING_STATUSES = ['active', 'paused', 'archived'] as const
export type ListingStatus = (typeof LISTING_STATUSES)[number]
export const listingStatusSchema = z.enum(LISTING_STATUSES)

export const LISTING_STATUS_LABELS: Record<ListingStatus, string> = {
  active: 'Selling',
  paused: 'Paused',
  archived: 'Removed',
}

// ---------------------------------------------------------------------------
// Seller listings
// ---------------------------------------------------------------------------

export const sellerListingSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  /** Present for admins only. Buyer-facing responses never carry this shape at all. */
  orgName: z.string().nullable(),
  product: productSummarySchema,
  sellerSku: z.string().nullable(),
  /** Minimum the shop will sell in one go, scaled by the unit's decimals. */
  moq: z.number().int(),
  leadTimeDays: z.number().int(),
  stockQty: z.number().int().nullable(),
  currency: currencySchema,
  tiers: z.array(priceTierSchema),
  note: z.string().nullable(),
  status: listingStatusSchema,
  updatedAt: z.number().int(),
  version: z.number().int(),
})
export type SellerListing = z.infer<typeof sellerListingSchema>

/**
 * The editor asks for "from this quantity, this price" and derives the rest.
 * Two fields per row instead of three — `normalizeTiers` fills in each `maxQty`
 * from the next row's `minQty`, which also makes gaps and overlaps unrepresentable.
 */
export const tierRowSchema = z.object({
  minQty: z.number().int().min(0),
  unitPriceMinor: z.number().int().min(0),
  label: z.string().trim().max(60).nullish(),
})
export const tierRowListSchema = z
  .array(tierRowSchema)
  .min(1, 'Add at least one price row.')
  .max(12)
export type TierRowInput = z.infer<typeof tierRowSchema>

export const upsertSellerListingSchema = z.object({
  productId: z.string().min(1, 'Pick an item.'),
  sellerSku: z.string().trim().max(60).nullish(),
  moq: z.number().int().min(0).default(0),
  leadTimeDays: z.number().int().min(0).max(365).default(0),
  stockQty: z.number().int().min(0).nullish(),
  tiers: tierRowListSchema,
  note: z.string().trim().max(500).nullish(),
  status: listingStatusSchema.optional(),
})
export type UpsertSellerListingInput = z.infer<typeof upsertSellerListingSchema>

// ---------------------------------------------------------------------------
// Admin listings — the customer-facing side
// ---------------------------------------------------------------------------

/**
 * Where the goods come from when a customer orders.
 * `platform_stock` draws down our own shelf; `seller_stock` is drop-shipped from a
 * shop; `made_to_order` promises nothing until a price check comes back.
 */
export const AVAILABILITY_MODES = ['platform_stock', 'seller_stock', 'made_to_order'] as const
export type AvailabilityMode = (typeof AVAILABILITY_MODES)[number]
export const availabilityModeSchema = z.enum(AVAILABILITY_MODES)

export const AVAILABILITY_LABELS: Record<AvailabilityMode, string> = {
  platform_stock: 'From our stock',
  seller_stock: 'From a supplier shop',
  made_to_order: 'Ordered in for you',
}

export const ADMIN_LISTING_STATUSES = ['draft', 'published', 'unpublished', 'archived'] as const
export type AdminListingStatus = (typeof ADMIN_LISTING_STATUSES)[number]
export const adminListingStatusSchema = z.enum(ADMIN_LISTING_STATUSES)

export const ADMIN_LISTING_STATUS_LABELS: Record<AdminListingStatus, string> = {
  draft: 'Draft',
  published: 'Live',
  unpublished: 'Hidden',
  archived: 'Removed',
}

export const adminListingSchema = z.object({
  id: z.string(),
  product: productSummarySchema,
  status: adminListingStatusSchema,
  availability: availabilityModeSchema,
  moq: z.number().int(),
  maxOrderQty: z.number().int().nullable(),
  leadTimeDays: z.number().int(),
  taxBps: z.number().int(),
  currency: currencySchema,
  tiers: z.array(priceTierSchema),
  /** Admin-only. The best live seller cost, for the margin readout. */
  bestCostMinor: z.number().int().nullable(),
  /** Admin-only. Margin at the first tier, in basis points. */
  marginBps: z.number().int().nullable(),
  onHandQty: z.number().int().nullable(),
  availableQty: z.number().int().nullable(),
  publishedAt: z.number().int().nullable(),
  updatedAt: z.number().int(),
  version: z.number().int(),
})
export type AdminListing = z.infer<typeof adminListingSchema>

export const upsertAdminListingSchema = z.object({
  productId: z.string().min(1, 'Pick an item.'),
  availability: availabilityModeSchema.default('made_to_order'),
  moq: z.number().int().min(0).default(0),
  maxOrderQty: z.number().int().min(1).nullish(),
  leadTimeDays: z.number().int().min(0).max(365).default(0),
  taxBps: taxBpsSchema.default(DEFAULT_VAT_BPS),
  tiers: tierRowListSchema,
  status: adminListingStatusSchema.optional(),
})
export type UpsertAdminListingInput = z.infer<typeof upsertAdminListingSchema>

// ---------------------------------------------------------------------------
// The buyer's view — deliberately a different shape, not a filtered one
// ---------------------------------------------------------------------------

/**
 * What a customer sees. There is no seller field to forget to strip, because the
 * type has none: stock becomes a coarse signal and cost never appears (D4).
 */
export const STOCK_SIGNALS = ['in_stock', 'limited', 'to_order'] as const
export type StockSignal = (typeof STOCK_SIGNALS)[number]

export const STOCK_SIGNAL_LABELS: Record<StockSignal, string> = {
  in_stock: 'Ready to go',
  limited: 'Limited stock',
  to_order: 'Ordered in for you',
}

export const catalogItemSchema = z.object({
  productId: z.string(),
  name: z.string(),
  brand: z.string().nullable(),
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  unitCode: z.string(),
  packQty: z.number().int().nullable(),
  packUnitCode: z.string().nullable(),
  imageAssetId: z.string().nullable(),
  currency: currencySchema,
  tiers: z.array(priceTierSchema),
  moq: z.number().int(),
  maxOrderQty: z.number().int().nullable(),
  leadTimeDays: z.number().int(),
  taxBps: z.number().int(),
  stockSignal: z.enum(STOCK_SIGNALS),
})
export type CatalogItem = z.infer<typeof catalogItemSchema>

export const catalogSortSchema = z.enum(['relevance', 'price_asc', 'price_desc', 'name'])
export type CatalogSort = z.infer<typeof catalogSortSchema>

export const catalogQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  categoryId: z.string().max(64).optional(),
  sort: catalogSortSchema.default('relevance'),
  inStockOnly: z.coerce.boolean().optional(),
})
export type CatalogQuery = z.infer<typeof catalogQuerySchema>

export const catalogItemDetailSchema = catalogItemSchema.extend({
  description: z.string().nullable(),
  model: z.string().nullable(),
  /** Other things in the same category, so a dead end always has a way out. */
  related: z.array(catalogItemSchema),
})
export type CatalogItemDetail = z.infer<typeof catalogItemDetailSchema>

// ---------------------------------------------------------------------------
// Platform stock and the stock ledger
// ---------------------------------------------------------------------------

export const STOCK_MOVEMENT_KINDS = [
  'receive',
  'reserve',
  'release',
  'issue',
  'adjust',
  'return',
] as const
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number]
export const stockMovementKindSchema = z.enum(STOCK_MOVEMENT_KINDS)

export const STOCK_MOVEMENT_LABELS: Record<StockMovementKind, string> = {
  receive: 'Took delivery',
  reserve: 'Set aside for an order',
  release: 'Put back on the shelf',
  issue: 'Sent out',
  adjust: 'Corrected the count',
  return: 'Came back',
}

export const platformStockSchema = z.object({
  productId: z.string(),
  product: productSummarySchema,
  onHandQty: z.number().int(),
  reservedQty: z.number().int(),
  /** onHand - reserved. Derived, never stored as the source of truth. */
  availableQty: z.number().int(),
  reorderPoint: z.number().int(),
  lowStock: z.boolean(),
  updatedAt: z.number().int(),
  version: z.number().int(),
})
export type PlatformStock = z.infer<typeof platformStockSchema>

export const stockMovementSchema = z.object({
  id: z.string(),
  productId: z.string(),
  productName: z.string(),
  unitCode: z.string(),
  kind: stockMovementKindSchema,
  /** Signed delta applied to on-hand. Reservations move `reservedAfter` instead. */
  qty: z.number().int(),
  balanceAfter: z.number().int(),
  reservedAfter: z.number().int(),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  note: z.string().nullable(),
  actorName: z.string().nullable(),
  createdAt: z.number().int(),
})
export type StockMovement = z.infer<typeof stockMovementSchema>

export const receiveStockSchema = z.object({
  productId: z.string().min(1),
  qty: z.number().int().min(1, 'Enter how much arrived.'),
  note: z.string().trim().max(300).nullish(),
})
export type ReceiveStockInput = z.infer<typeof receiveStockSchema>

/**
 * A correction states the count you just made, not the difference. Asking a
 * storekeeper for a delta is asking them to do arithmetic on a ladder.
 */
export const adjustStockSchema = z.object({
  productId: z.string().min(1),
  countedQty: z.number().int().min(0),
  reason: z.string().trim().min(2, 'Say what happened.').max(300),
})
export type AdjustStockInput = z.infer<typeof adjustStockSchema>

export const setReorderPointSchema = z.object({
  productId: z.string().min(1),
  reorderPoint: z.number().int().min(0),
})
export type SetReorderPointInput = z.infer<typeof setReorderPointSchema>

/** Shared by both tier editors, so the two ladders can never validate differently. */
export const tierEditorPayloadSchema = z.object({
  unitCode: unitCodeSchema,
  tiers: priceTierListSchema,
})
